import { useEffect, useRef, useState } from "react";
import getWindow from "../../utils/window";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useHeroGalleryVideo } from "./HeroGalleryVideoContext";
import { useLiveRegion } from "../../context/liveRegion/LiveRegionContext";
import { useViewport } from "../../context/viewport/ViewportContext";
import { SMALL_VIEWPORT } from "../../constants";
import styles from "./HeroGalleryVideoPlayer.scss";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import Button from "../buttons/button/Button";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";

const LOOPS_UNTIL_CLOSE = 2;

const HeroGalleryVideoPlayer: React.FC = () => {
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };
  const { isVideoVisible, videoUrl, closeVideo, triggerRef } =
    useHeroGalleryVideo();
  const { updateLiveRegion } = useLiveRegion();
  const { viewportCategory } = useViewport();
  const onSmallViewport = viewportCategory === SMALL_VIEWPORT;
  const [isVideoError, setIsVideoError] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const refreshButtonRef = useRef<HTMLButtonElement>(null);
  const loopCountRef = useRef(0);

  useEffect(() => {
    if (isVideoError && isVideoVisible) {
      refreshButtonRef.current?.focus();
    }
  }, [isVideoError, isVideoVisible]);

  useEffect(() => {
    const video = videoRef.current;

    if (!isVideoVisible || !video) {
      return;
    }

    loopCountRef.current = 0;

    const handleFullscreenExit = () => {
      if (onSmallViewport) {
        closeVideo();
      }
    };

    const handleDocumentFullscreenChange = () => {
      if (onSmallViewport && !getWindow()?.document.fullscreenElement) {
        closeVideo();
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeVideo();
        triggerRef.current?.focus();
      }
    };

    video.addEventListener("webkitendfullscreen", handleFullscreenExit);

    getWindow()?.document.addEventListener(
      "fullscreenchange",
      handleDocumentFullscreenChange,
    );
    getWindow()?.document.addEventListener("keydown", handleKeydown);

    return () => {
      video.removeEventListener("webkitendfullscreen", handleFullscreenExit);
      getWindow()?.document.removeEventListener(
        "fullscreenchange",
        handleDocumentFullscreenChange,
      );
      getWindow()?.document.removeEventListener("keydown", handleKeydown);
    };
  }, [isVideoVisible, closeVideo, onSmallViewport, triggerRef]);

  if (!isVideoVisible || !videoUrl) {
    return null;
  }

  const handleEnded = () => {
    const video = videoRef.current;
    loopCountRef.current += 1;

    if (loopCountRef.current < LOOPS_UNTIL_CLOSE && video) {
      video.currentTime = 0;
      video.play();
      return;
    }

    closeVideo();
    triggerRef.current?.focus();
  };

  return (
    <div className={styles.container}>
      {isVideoError ? (
        <div className={styles.error} role="alert">
          <div className={styles.copy}>
            <p className={styles.errorHeading}>
              {getTranslation("pdp_media_video_player_error_header")}
            </p>
            <p>{getTranslation("pdp_media_video_player_error_message")}</p>
          </div>
          <Button
            type={BUTTON_TYPES.primary}
            handleClick={() => setIsVideoError(false)}
            additionalClasses={styles.refreshButton}
            buttonRef={refreshButtonRef}
          >
            {getTranslation("pdp_media_video_player_refresh_button")}
          </Button>
          <Button
            type={BUTTON_TYPES.naked}
            handleClick={() => {
              closeVideo();
              triggerRef.current?.focus();
            }}
            additionalClasses={styles.closeButton}
          >
            {getTranslation("pdp_media_video_player_close_button")}
          </Button>
        </div>
      ) : (
        <video
          ref={videoRef}
          className={styles.video}
          src={videoUrl}
          playsInline={!onSmallViewport}
          autoPlay
          onEnded={handleEnded}
          onError={(errorEvent) => {
            setIsVideoError(true);
            logVideoError(errorEvent.currentTarget.error);
          }}
          onPlay={(event) => {
            setIsVideoError(false);

            if (loopCountRef.current === 0) {
              updateLiveRegion(
                getTranslation("accessibility_pdp_video_playing"),
              );
            }

            if (onSmallViewport) {
              event.currentTarget.requestFullscreen?.();
            }
          }}
          muted
          controls
        />
      )}
    </div>
  );
};

function logVideoError(error: unknown) {
  const message = "Hero gallery video playback error";
  if (error instanceof MediaError) {
    logError(ERROR_TYPE.NonCriticalAppError, {
      message,
      errorCode: error.code,
      errorMessage: error.message,
    });
  } else if (error instanceof Error) {
    logError(ERROR_TYPE.NonCriticalAppError, {
      message,
      errorMessage: error.message,
    });
  }
}

export default HeroGalleryVideoPlayer;
